import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Banner,
  Button,
  Card,
  Container,
  HStack,
  Heading,
  IconButton,
  Input,
  Spinner,
  Tabs,
  Text,
  VStack,
} from 'wss3-forge'
import { Delete16Regular, Search20Regular } from '@fluentui/react-icons'
import { deleteMemory, listMemories, type Memory } from '../lib/api.js'

const TAB_ORDER = ['all', 'fact', 'preference', 'context', 'note'] as const
type MemoryTab = (typeof TAB_ORDER)[number]

const TAB_LABEL: Record<MemoryTab, string> = {
  all: 'Toutes',
  fact: 'Faits',
  preference: 'Préférences',
  context: 'Contexte',
  note: 'Notes',
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const sec = Math.round(diff / 1000)
  if (sec < 60) return 'à l’instant'
  if (sec < 3600) return `il y a ${Math.round(sec / 60)} min`
  if (sec < 86400) return `il y a ${Math.round(sec / 3600)} h`
  const days = Math.round(sec / 86400)
  if (days < 30) return `il y a ${days} j`
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function MemoriesPage() {
  const [rawQuery, setRawQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [tab, setTab] = useState<MemoryTab>('all')

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(rawQuery.trim()), 250)
    return () => window.clearTimeout(t)
  }, [rawQuery])

  const qc = useQueryClient()
  const memoriesQuery = useQuery<Memory[]>({
    queryKey: ['memories', debounced],
    queryFn: () => listMemories(debounced ? { q: debounced, limit: 200 } : { limit: 200 }),
  })

  const del = useMutation({
    mutationFn: (id: number) => deleteMemory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memories'] }),
  })

  const all = memoriesQuery.data ?? []

  // Counts power the tab badges. Computed on the full set so switching tabs
  // doesn't refetch anything.
  const counts = useMemo(() => {
    const c: Record<MemoryTab, number> = { all: all.length, fact: 0, preference: 0, context: 0, note: 0 }
    for (const m of all) {
      const key = (TAB_ORDER as readonly string[]).includes(m.type) ? (m.type as MemoryTab) : 'note'
      c[key] += 1
    }
    return c
  }, [all])

  const memories = useMemo(
    () => (tab === 'all' ? all : all.filter((m) => (m.type === tab) || (tab === 'note' && !TAB_ORDER.includes(m.type as MemoryTab)))),
    [all, tab],
  )

  const empty = !memoriesQuery.isLoading && memories.length === 0

  const subtitle = memoriesQuery.isLoading
    ? 'Chargement…'
    : debounced
      ? `${memories.length} résultat(s) pour « ${debounced} »`
      : `${memories.length} mémoire(s) actives`

  return (
    <Container maxWidth="lg" py="2xl">
      <VStack gap="lg" fullWidth>
        <VStack gap="sm">
          <Heading level={2}>Mémoires</Heading>
          <Text color="muted">{subtitle}</Text>
        </VStack>

        <Input
          value={rawQuery}
          onChange={setRawQuery}
          placeholder="Filtrer par mot-clé..."
          icon={<Search20Regular />}
          clearable
          aria-label="Recherche mémoire"
        />

        <Tabs
          tabs={TAB_ORDER.map((id) => ({ id, label: TAB_LABEL[id], count: counts[id] }))}
          value={tab}
          onChange={(id) => setTab(id as MemoryTab)}
          variant="underline"
        />

        {memoriesQuery.error && (
          <Banner variant="error" title="Échec du chargement">
            {String((memoriesQuery.error as Error).message)}
          </Banner>
        )}

        {memoriesQuery.isLoading && (
          <HStack gap="sm" align="center">
            <Spinner size="sm" />
            <Text size="sm" color="muted">Chargement des mémoires…</Text>
          </HStack>
        )}

        {empty && (
          <Banner variant="info" title={`Aucune mémoire ${debounced ? 'ne correspond' : 'pour l’instant'}.`}>
            {!debounced ? 'Demande à Stark de retenir quelque chose pour commencer.' : null}
          </Banner>
        )}

        <VStack gap="sm" fullWidth>
          {memories.map((m) => (
            <Card key={m.id} padding="md">
              <VStack gap="xs" fullWidth>
                <HStack justify="between" align="start" gap="sm" fullWidth>
                  <HStack gap="xs" align="center">
                    <Badge variant="default">{m.type}</Badge>
                    <Text size="xs" color="muted">
                      {formatRelative(m.last_accessed_at)} · {m.access_count}× accédée
                    </Text>
                  </HStack>
                  <IconButton
                    icon={<Delete16Regular />}
                    tooltip="Oublier cette mémoire"
                    aria-label={`Oublier la mémoire ${m.id}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => del.mutate(m.id)}
                    disabled={del.isPending}
                  />
                </HStack>
                <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{m.content}</Text>
              </VStack>
            </Card>
          ))}
        </VStack>
      </VStack>
    </Container>
  )
}
